import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Trash2, Pencil, Clock, ClipboardList } from "lucide-react";

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function WorkReport() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const L = lang === "el";
  const isOwner = user?.role === "company_owner";
  const isRep = user?.role === "sales_rep";

  const [reps, setReps] = useState([]);
  const [selectedRep, setSelectedRep] = useState("");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const blank = { date: todayISO(), task: "", description: "", hours_spent: 1 };
  const [form, setForm] = useState(blank);

  const load = async () => {
    const params = new URLSearchParams();
    if (isOwner && selectedRep) params.set("user_id", selectedRep);
    const { data } = await api.get(`/work-reports${params.toString() ? `?${params}` : ""}`);
    setList(data);
  };

  useEffect(() => {
    if (isOwner) {
      api.get("/users").then((r) => {
        const onlyReps = (r.data || []).filter((u) => u.role === "sales_rep");
        setReps(onlyReps);
        if (onlyReps.length && !selectedRep) setSelectedRep(onlyReps[0].id);
      });
    }
  }, [isOwner]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedRep, isOwner, isRep]);

  const openCreate = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ date: r.date, task: r.task, description: r.description || "", hours_spent: r.hours_spent }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, hours_spent: parseFloat(form.hours_spent) || 0 };
    if (editing) await api.patch(`/work-reports/${editing.id}`, payload);
    else await api.post("/work-reports", payload);
    setOpen(false); load();
  };

  const del = async (id) => {
    if (!window.confirm(L ? "Διαγραφή αναφοράς;" : "Delete report?")) return;
    await api.delete(`/work-reports/${id}`); load();
  };

  // Group by date
  const byDate = useMemo(() => {
    const m = {};
    for (const r of list) {
      (m[r.date] = m[r.date] || []).push(r);
    }
    return Object.entries(m).sort((a, b) => b[0].localeCompare(a[0]));
  }, [list]);

  const totalHours = list.reduce((s, r) => s + (r.hours_spent || 0), 0);

  return (
    <div className="space-y-6" data-testid="work-report-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
            <ClipboardList className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
            {L ? "Καταγραφή εργασίας" : "Activity log"}
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">
            {L ? "Αναφορά εργασίας" : "Work report"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isRep
              ? (L ? "Κατάγραψε καθημερινά τις εργασίες που έκανες και τις εργατώρες που χρειάστηκαν" : "Log daily tasks and hours spent")
              : (L ? "Αναφορές εργασίας ανά πωλητή" : "Per-rep work reports")}
          </p>
        </div>
        <div className="flex items-end gap-3">
          {isOwner && (
            <div className="min-w-[240px]">
              <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{L ? "Πωλητής" : "Sales rep"}</label>
              <select
                data-testid="work-report-rep-select"
                value={selectedRep}
                onChange={(e) => setSelectedRep(e.target.value)}
                className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
              >
                <option value="">{L ? "— Επιλέξτε —" : "— Select —"}</option>
                {reps.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          )}
          {isRep && (
            <button data-testid="work-report-create" onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold">
              <Plus className="w-4 h-4" /> {L ? "Νέα καταχώρηση" : "New entry"}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Kpi label={L ? "Συνολικές καταχωρήσεις" : "Total entries"} value={list.length} />
        <Kpi label={L ? "Συνολικές εργατώρες" : "Total hours"} value={`${totalHours.toFixed(1)}h`} accent="#16A34A" />
        <Kpi label={L ? "Μέσος όρος / ημέρα" : "Avg / day"} value={byDate.length ? `${(totalHours / byDate.length).toFixed(1)}h` : "0h"} accent="#0EA5E9" />
      </div>

      <div className="space-y-5" data-testid="work-report-list">
        {byDate.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center text-slate-500">
            {isRep
              ? (L ? "Δεν έχεις καταγράψει εργασίες ακόμη. Πάτα 'Νέα καταχώρηση'." : "No entries yet. Click 'New entry'.")
              : (L ? "Ο επιλεγμένος πωλητής δεν έχει καταγράψει εργασίες." : "This rep has no entries.")}
          </div>
        ) : byDate.map(([date, rows]) => {
          const dayTotal = rows.reduce((s, r) => s + (r.hours_spent || 0), 0);
          return (
            <div key={date} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <header className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <div className="font-semibold text-slate-900">
                  {new Date(date).toLocaleDateString(L ? "el-GR" : "en-GB", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                </div>
                <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">{dayTotal.toFixed(1)}h</span>
              </header>
              <ul className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <li key={r.id} className="px-5 py-3 flex items-start gap-3" data-testid={`work-report-row-${r.id}`}>
                    <div className="w-10 h-10 rounded-md bg-[#0052CC]/10 text-[#0052CC] flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="font-semibold text-slate-900">{r.task}</div>
                        <div className="text-sm font-mono text-slate-700 whitespace-nowrap">{r.hours_spent}h</div>
                      </div>
                      {r.description && <div className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap">{r.description}</div>}
                      {isOwner && r.user_name && <div className="text-[11px] text-slate-500 mt-1">— {r.user_name}</div>}
                    </div>
                    {isRep && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button data-testid={`work-report-edit-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 text-slate-400 hover:text-[#0052CC]"><Pencil className="w-4 h-4" /></button>
                        <button data-testid={`work-report-delete-${r.id}`} onClick={() => del(r.id)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-lg max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">
                {editing ? (L ? "Επεξεργασία" : "Edit") : (L ? "Νέα καταχώρηση" : "New entry")}
              </h3>
              <button onClick={() => setOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="px-6 py-4 space-y-3" data-testid="work-report-form">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Ημερομηνία" : "Date"}</label>
                <input data-testid="wr-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Εργασία" : "Task"}</label>
                <input data-testid="wr-task" required value={form.task} onChange={(e) => setForm({ ...form, task: e.target.value })} placeholder={L ? "π.χ. Επικοινωνία με τυροκομεία" : "e.g. Outreach to cheese makers"} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Περιγραφή" : "Description"}</label>
                <textarea data-testid="wr-description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" placeholder={L ? "Τι έκανες αναλυτικά;" : "What did you do?"} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Εργατώρες" : "Hours spent"}</label>
                <input data-testid="wr-hours" type="number" step="0.25" min="0" max="24" required value={form.hours_spent} onChange={(e) => setForm({ ...form, hours_spent: e.target.value })} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-slate-200 rounded-md text-sm font-medium">{L ? "Άκυρο" : "Cancel"}</button>
                <button type="submit" data-testid="wr-submit" className="px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold">
                  {editing ? (L ? "Αποθήκευση" : "Save") : (L ? "Αποθήκευση" : "Save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, accent = "#0052CC" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">{label}</div>
      <div className="mt-1 font-heading text-2xl font-bold" style={{ color: accent }}>{value}</div>
    </div>
  );
}
